import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@geely/ui';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { StateSelector } from '@/app/components/booking/StateSelector';
import { CitySelector } from '@/app/components/booking/CitySelector';
import { StoreSelector } from '@/app/components/booking/StoreSelector';
import { DateTimeScheduler } from '@/app/components/booking/DateTimeScheduler';
import { ConflictAlert } from './ConflictAlert';
import type { Store } from '@geely/shared';
import type { ConflictErrorResponse } from '@/lib/api';
import type { BookingSubStep } from '../hooks/useBookingWizard';

interface StoreSelectionStepProps {
  subStep: BookingSubStep;
  stores: Store[];
  isLoadingStores: boolean;
  selectedState: string;
  selectedCity: string;
  selectedStoreId: string;
  selectedDate: string;
  selectedTime: string;
  conflictError: ConflictErrorResponse | null;
  onStateSelect: (state: string) => void;
  onCitySelect: (city: string) => void;
  onStoreSelect: (storeId: string) => void;
  onDateTimeSelect: (date: string, time: string) => void;
  onBackSubStep: () => void;
  onNext: () => void;
  onSelectAlternativeTime: (time: string) => void;
}

export function StoreSelectionStep({
  subStep,
  stores,
  isLoadingStores,
  selectedState,
  selectedCity,
  selectedStoreId,
  selectedDate,
  selectedTime,
  conflictError,
  onStateSelect,
  onCitySelect,
  onStoreSelect,
  onDateTimeSelect,
  onBackSubStep,
  onNext,
  onSelectAlternativeTime,
}: StoreSelectionStepProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Informações do Agendamento</CardTitle>
        <CardDescription>
          {subStep === 1 && 'Passo 1 de 4 - Selecione o estado onde deseja ser atendido'}
          {subStep === 2 && 'Passo 2 de 4 - Selecione a cidade'}
          {subStep === 3 && 'Passo 3 de 4 - Selecione a loja de sua preferência'}
          {subStep === 4 && 'Passo 4 de 4 - Escolha o dia e horário disponível'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sub-step 1: State Selection */}
        {subStep === 1 && (
          <StateSelector
            stores={stores}
            isLoading={isLoadingStores}
            selectedState={selectedState}
            onSelectState={onStateSelect}
          />
        )}

        {/* Sub-step 2: City Selection */}
        {subStep === 2 && (
          <CitySelector
            stores={stores}
            selectedState={selectedState}
            selectedCity={selectedCity}
            onSelectCity={onCitySelect}
          />
        )}

        {/* Sub-step 3: Store Selection */}
        {subStep === 3 && (
          <StoreSelector
            stores={stores}
            selectedState={selectedState}
            selectedCity={selectedCity}
            selectedStoreId={selectedStoreId}
            onSelectStore={onStoreSelect}
          />
        )}

        {/* Sub-step 4: Date/Time Selection */}
        {subStep === 4 && (
          <>
            {/* CAMADA 4: Display conflict error with alternative slots */}
            {conflictError && (
              <ConflictAlert
                conflictError={conflictError}
                onSelectAlternativeTime={onSelectAlternativeTime}
              />
            )}

            <DateTimeScheduler
              selectedStoreId={selectedStoreId}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              stores={stores}
              onSelectDateTime={onDateTimeSelect}
            />
          </>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          {subStep > 1 && (
            <Button variant="outline" onClick={onBackSubStep}>
              <ArrowLeft className="size-4 mr-2" />
              Voltar
            </Button>
          )}
          {subStep === 4 && selectedDate && selectedTime && (
            <Button onClick={onNext} className="ml-auto">
              Próximo
              <ArrowRight className="size-4 ml-2" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
